import React, { useState, useEffect } from 'react';

// material-ui
import { CssBaseline, Divider, Grid, Stack, FormControl, InputAdornment, OutlinedInput, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Link } from '@mui/material';
import { Search } from '@mui/icons-material';

//components
import AlertInfo from '../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../components/elements/AlertInfoDialog';
import TopBar from '../../components/nav/TopBar';
import OcpfWatchSubscriber from './dataObject/OcpfWatchSubscriber';
import OcpfWatchSubscriptionTypeCount from './dataObject/OcpfWatchSubscriptionTypeCount';
import SubscriptionTypeChart from './components/SubscriptionTypeChart';

const OcpfWatchSubscribersApp = (): JSX.Element => {

    //set up browser title
    const pageTitle = 'Extranet - OCPF Watch Subscribers';
    document.title = pageTitle;

    const applicationName = 'OCPF Watch Subscribers';

    //set up state for the app
    const [subscribers, setSubscribers] = useState<OcpfWatchSubscriber[]>();
    const [allSubscribers, setAllSubscribers] = useState<OcpfWatchSubscriber[]>();
    const [typeCounts, setTypeCounts] = useState<OcpfWatchSubscriptionTypeCount[]>();
    const totalSubscriptions = typeCounts?.reduce((sum, t) => sum + t.subscriptionTypeCount, 0);

    //payload
    useEffect(() => {
        getSubscribers();
        getSubscriptionTypeCounts();
    }, []);


    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const updateSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        let searchPhrase = event.target.value;

        setTimeout(() => {
            if (!searchPhrase) {
                setSubscribers(allSubscribers);
            }
            else {
                let filteredRecords = allSubscribers?.filter(
                    subscriber => subscriber.fullName.toLowerCase().includes(searchPhrase.toLowerCase())
                        || subscriber.emailAddress.toLowerCase().includes(searchPhrase.toLowerCase())
                        || subscriber.organizationName.toLowerCase().includes(searchPhrase.toLowerCase())
                );
                setSubscribers(filteredRecords);
            }
        }, 1000)
    }

    const getSubscribers = async () => {
        await fetch(`/StaffTools/GetOcpfWatchSubscribers`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetOcpfWatchSubscribers',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setSubscribers(result.result);
                    setAllSubscribers(result.result);
                }
                }
            )
    }

    const getSubscriptionTypeCounts = async () => {
        await fetch(`/StaffTools/GetOcpfWatchSubscriptionTypeCounts`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetOcpfWatchSubscriptionTypeCounts',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setTypeCounts(result.result);
                }
            })
    }

    return (
        <div>
            <Grid container sx={{ display: 'flex' }}>
                <CssBaseline />
                <TopBar applicationName={applicationName}></TopBar>
            </Grid>
            <Grid item xs={12} marginBottom={1}>
                <Divider />
            </Grid>
            <Grid container sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', marginBottom: '15px' }} >
                <Grid item xs={10}>
                    <Stack marginBottom={3}>
                        <Typography variant="h6" fontWeight={400}>OCPF Watch subscribers: {subscribers?.length} subscribers, {totalSubscriptions} subscriptions</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} >
                        <FormControl sx={{ minWidth: 400, marginTop: "5px !important" }} variant="outlined" size="small">
                            <OutlinedInput
                                aria-label='Search Subscriber...'
                                title='Search Subscriber'
                                placeholder={"search name, email, or organization..."}
                                onChange={updateSearchInput}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </Stack>
                </Grid>
            </Grid>
            {
                typeCounts && typeCounts.length > 0 &&
                <Grid container sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }} >
                    <Grid item xs={10}>
                        <Stack maxWidth={700}>
                            <SubscriptionTypeChart typeCounts={typeCounts}></SubscriptionTypeChart>
                        </Stack>
                    </Grid>
                </Grid>
            }
            {
                subscribers && subscribers.length > 0 &&
                <Grid container marginTop={5} display={'flex'} justifyContent={'flex-start'} >
                    <Grid item xs={1}></Grid>
                    <Grid item xs={10}>
                            <TableContainer>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><Typography variant='body1' fontWeight={550}>Name</Typography></TableCell>
                                            <TableCell><Typography variant='body1' fontWeight={550}>Email</Typography></TableCell>
                                            <TableCell><Typography variant='body1' fontWeight={550}>Organization</Typography></TableCell>
                                            <TableCell><Typography variant='body1' fontWeight={550}>Subscribed Date</Typography></TableCell>
                                            <TableCell><Typography variant='body1' fontWeight={550}>Subscriptions #</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subscribers.map((subscriber: OcpfWatchSubscriber) =>
                                            <TableRow key={subscriber.userId}>
                                                <TableCell>
                                                    <Typography variant="body2">{subscriber.fullName}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" component={Link} href={`mailto:${subscriber.emailAddress}`} underline="hover">{subscriber.emailAddress}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{subscriber.organizationName}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{subscriber.firstSubscriptionDate}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{subscriber.subscriptionCount}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                    </Grid>
                </Grid>
            }
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
    );
};

export default OcpfWatchSubscribersApp;
